/**
 * Element Configuration Components for the Course Builder.
 * Each config renders the properties panel for a specific element type.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MediaUploadField } from '@/components/builder/MediaUploadField';
import { mediaUrl } from '@/lib/mediaUrl';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, ChevronDown, ChevronUp, X } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import { QuestionBankPicker } from '../QuestionBankPicker';

// Video Element Config
const VideoConfig = ({ config, onChange }) => {
  const videoConfig = config?.video || {};

  const handleChange = (key, value) => {
    onChange({
      config: {
        ...config,
        video: { ...videoConfig, [key]: value },
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Video Source</Label>
        <Select
          value={videoConfig.source || 'youtube'}
          onValueChange={(v) => handleChange('source', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="youtube">YouTube</SelectItem>
            <SelectItem value="vimeo">Vimeo</SelectItem>
            <SelectItem value="direct">Direct Upload</SelectItem>
            <SelectItem value="synthesia">Synthesia</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {videoConfig.source === 'direct' ? (
        <MediaUploadField label="Video File" kind="video" value={videoConfig.url} onChange={(v) => handleChange('url', v)} testId="video-file" required />
      ) : (
        <div className="space-y-2">
          <Label>Video URL <span className="text-red-500">*</span></Label>
          <Input
            value={videoConfig.url || ''}
            onChange={(e) => handleChange('url', e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            data-testid="video-url-input"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label>Completion Threshold (%)</Label>
        <Input
          type="number"
          min="0"
          max="100"
          value={(videoConfig.completion_threshold || 0.9) * 100}
          onChange={(e) => handleChange('completion_threshold', parseInt(e.target.value) / 100)}
        />
        <p className="text-xs text-slate-500">
          Percentage of video that must be watched to mark as complete
        </p>
      </div>

      {['youtube', 'vimeo'].includes(videoConfig.source || 'youtube') ? (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-xs text-blue-700" data-testid="video-captions-platform-note">
          <strong>Captions:</strong> {videoConfig.source === 'vimeo' ? 'Vimeo' : 'YouTube'} videos use the platform's own captions. Add or upload captions in your {videoConfig.source === 'vimeo' ? 'Vimeo' : 'YouTube'} account; learners can switch them on with the CC button.
        </div>
      ) : (
        <MediaUploadField label="Captions (.vtt / .srt)" kind="captions" value={videoConfig.captions_url} onChange={(v) => handleChange('captions_url', v)} testId="video-captions" />
      )}

      <div className="space-y-2">
        <Label>Transcript</Label>
        <div data-testid="video-transcript">
          <RichTextEditor content={videoConfig.transcript || ''} onChange={(html) => handleChange('transcript', html)} placeholder="Full text transcript of the video for accessibility..." />
        </div>
      </div>
    </div>
  );
};

// Quiz Element Config
const QuizConfig = ({ config, onChange }) => {
  const quizConfig = config?.quiz || { questions: [] };

  const handleChange = (key, value) => {
    onChange({
      config: {
        ...config,
        quiz: { ...quizConfig, [key]: value },
      },
    });
  };

  const addQuestion = (type) => {
    const newQuestion = {
      id: Date.now().toString(),
      type,
      question_text: '',
      options: type === 'true_false' ? [
        { id: '1', text: 'True', isCorrect: false },
        { id: '2', text: 'False', isCorrect: false },
      ] : [],
      correct_answer: null,
      points: 1,
    };
    handleChange('questions', [...(quizConfig.questions || []), newQuestion]);
  };

  const updateQuestion = (index, updates) => {
    const questions = [...(quizConfig.questions || [])];
    questions[index] = { ...questions[index], ...updates };
    handleChange('questions', questions);
  };

  const removeQuestion = (index) => {
    const questions = [...(quizConfig.questions || [])];
    questions.splice(index, 1);
    handleChange('questions', questions);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Passing Score (%)</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={quizConfig.passing_score || 70}
            onChange={(e) => handleChange('passing_score', parseInt(e.target.value))}
          />
        </div>
        <div className="space-y-2">
          <Label>Max Attempts</Label>
          <Input
            type="number"
            min="1"
            value={quizConfig.max_attempts || 3}
            onChange={(e) => handleChange('max_attempts', parseInt(e.target.value))}
          />
        </div>
      </div>

      <div className="p-3 rounded-lg border border-slate-200 space-y-3" data-testid="quiz-time-limit-section">
        <div className="flex items-center justify-between">
          <div>
            <Label>Time Limit</Label>
            <p className="text-xs text-slate-500">Off by default — learners get unlimited time (better for accessibility).</p>
          </div>
          <Switch
            checked={quizConfig.time_limit_enabled === true}
            onCheckedChange={(v) => onChange({ config: { ...config, quiz: { ...quizConfig, time_limit_enabled: v, time_limit_minutes: v ? (quizConfig.time_limit_minutes || 20) : quizConfig.time_limit_minutes } } })}
            data-testid="quiz-time-limit-toggle"
          />
        </div>
        {quizConfig.time_limit_enabled === true && (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="1"
              className="w-28"
              value={quizConfig.time_limit_minutes || ''}
              onChange={(e) => handleChange('time_limit_minutes', parseInt(e.target.value) || null)}
              placeholder="Minutes"
              data-testid="quiz-time-limit-input"
            />
            <span className="text-xs text-slate-500">minutes · timer starts when the learner clicks "Start Quiz"</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label>Final Assessment</Label>
          <p className="text-xs text-slate-500">Locked until learners finish every earlier activity; must be passed for the certificate.</p>
        </div>
        <Switch
          checked={quizConfig.is_final_assessment === true}
          onCheckedChange={(v) => handleChange('is_final_assessment', v)}
          data-testid="quiz-final-assessment-toggle"
        />
      </div>

      <div className="flex items-center justify-between">
        <Label>Randomise Questions</Label>
        <Switch
          checked={quizConfig.shuffle_questions !== false}
          onCheckedChange={(v) => handleChange('shuffle_questions', v)}
          data-testid="quiz-shuffle-questions-toggle"
        />
      </div>

      <div className="flex items-center justify-between">
        <Label>Randomise Answers</Label>
        <Switch
          checked={quizConfig.shuffle_answers === true}
          onCheckedChange={(v) => handleChange('shuffle_answers', v)}
          data-testid="quiz-shuffle-answers-toggle"
        />
      </div>

      <div className="flex items-center justify-between">
        <Label>Show Correct Answers</Label>
        <Switch
          checked={quizConfig.show_correct_answers !== false}
          onCheckedChange={(v) => handleChange('show_correct_answers', v)}
        />
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-1">
          <Label>Questions ({(quizConfig.questions || []).length})</Label>
          <QuestionBankPicker
            existingBankIds={(quizConfig.questions || []).map(q => q.question_bank_id).filter(Boolean)}
            onAdd={(qs) => handleChange('questions', [...(quizConfig.questions || []), ...qs])}
          />
        </div>
        <p className="text-xs text-slate-500 mb-3">New questions you write here are saved to the Question Bank automatically.</p>

        <div className="space-y-3">
          {(quizConfig.questions || []).map((q, idx) => (
            <QuestionEditor
              key={q.id}
              question={q}
              index={idx}
              onChange={(updates) => updateQuestion(idx, updates)}
              onRemove={() => removeQuestion(idx)}
            />
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => addQuestion('single_choice')}>
            + Single Choice
          </Button>
          <Button size="sm" variant="outline" onClick={() => addQuestion('multiple_choice')}>
            + Multiple Choice
          </Button>
          <Button size="sm" variant="outline" onClick={() => addQuestion('true_false')}>
            + True/False
          </Button>
          <Button size="sm" variant="outline" onClick={() => addQuestion('fill_blank')}>
            + Fill Blank
          </Button>
        </div>
      </div>
    </div>
  );
};

// Question Editor Component
const QuestionEditor = ({ question, index, onChange, onRemove }) => {
  const [expanded, setExpanded] = useState(true);

  const addOption = () => {
    const options = [...(question.options || [])];
    options.push({ id: Date.now().toString(), text: '', isCorrect: false });
    onChange({ options });
  };

  const updateOption = (optIdx, updates) => {
    const options = [...(question.options || [])];
    options[optIdx] = { ...options[optIdx], ...updates };
    
    // For single choice / true_false, unset other correct options
    if (['single_choice', 'true_false', 'image_based', 'scenario_based'].includes(question.type) && updates.isCorrect) {
      options.forEach((opt, i) => {
        if (i !== optIdx) opt.isCorrect = false;
      });
    }
    
    // Sync correct_answer / correct_answers from isCorrect flags
    if (['single_choice', 'true_false', 'image_based', 'scenario_based'].includes(question.type)) {
      const correctOpt = options.find(o => o.isCorrect);
      onChange({ options, correct_answer: correctOpt ? (correctOpt.value || correctOpt.id) : null });
    } else if (question.type === 'multiple_choice') {
      const correctVals = options.filter(o => o.isCorrect).map(o => o.value || o.id);
      onChange({ options, correct_answers: correctVals });
    } else {
      onChange({ options });
    }
  };

  const removeOption = (optIdx) => {
    const options = [...(question.options || [])];
    options.splice(optIdx, 1);
    onChange({ options });
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="p-3 pb-0">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-left"
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            <span className="font-medium text-sm">
              Q{index + 1}: {question.type.replace('_', ' ')}
            </span>
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 hover:text-red-600"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="p-3 pt-2 space-y-3">
          <div className="space-y-2">
            <Label className="text-xs">Question Text</Label>
            <Textarea
              value={question.question_text || ''}
              onChange={(e) => onChange({ question_text: e.target.value })}
              placeholder="Enter your question..."
              rows={2}
            />
          </div>
          <details className="text-xs" data-testid="question-image-section">
            <summary className="cursor-pointer text-[#20acf6]">Question image (optional)</summary>
            <div className="pt-2">
              <MediaUploadField label="Image" value={question.question_image_url} onChange={(v) => onChange({ question_image_url: v })}
                alt={question.question_image_alt} onAltChange={(v) => onChange({ question_image_alt: v })} testId="question-image" />
            </div>
          </details>

          {['single_choice', 'multiple_choice', 'true_false'].includes(question.type) && (
            <div className="space-y-2">
              <Label className="text-xs">Options</Label>
              {(question.options || []).map((opt, optIdx) => (
                <div key={opt.id} className="flex items-center gap-2">
                  <input
                    type={question.type === 'multiple_choice' ? 'checkbox' : 'radio'}
                    checked={opt.isCorrect}
                    onChange={(e) => updateOption(optIdx, { isCorrect: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Input
                    value={opt.text}
                    onChange={(e) => updateOption(optIdx, { text: e.target.value })}
                    placeholder={`Option ${optIdx + 1}`}
                    className="flex-1 h-8 text-sm"
                    disabled={question.type === 'true_false'}
                  />
                  {question.type !== 'true_false' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => removeOption(optIdx)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
              {question.type !== 'true_false' && (
                <Button size="sm" variant="outline" onClick={addOption} className="mt-2">
                  <Plus className="h-3 w-3 mr-1" /> Add Option
                </Button>
              )}
            </div>
          )}

          {question.type === 'fill_blank' && (
            <div className="space-y-2">
              <Label className="text-xs">Correct Answer</Label>
              <Input
                value={question.correct_answer || ''}
                onChange={(e) => onChange({ correct_answer: e.target.value })}
                placeholder="Enter the correct answer..."
              />
            </div>
          )}

          <div className="flex items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Points</Label>
              <Input
                type="number"
                min="1"
                value={question.points || 1}
                onChange={(e) => onChange({ points: parseInt(e.target.value) || 1 })}
                className="w-20 h-8"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Explanation (shown after answer)</Label>
            <Textarea
              value={question.explanation || ''}
              onChange={(e) => onChange({ explanation: e.target.value })}
              placeholder="Explain the correct answer..."
              rows={2}
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Flashcard Element Config
const FlashcardConfig = ({ config, onChange }) => {
  const flashcardConfig = config?.flashcard || { cards: [] };

  const handleChange = (key, value) => {
    onChange({
      config: {
        ...config,
        flashcard: { ...flashcardConfig, [key]: value },
      },
    });
  };

  const addCard = () => {
    const newCard = {
      id: Date.now().toString(),
      front_text: '',
      back_text: '',
    };
    handleChange('cards', [...(flashcardConfig.cards || []), newCard]);
  };

  const updateCard = (index, updates) => {
    const cards = [...(flashcardConfig.cards || [])];
    cards[index] = { ...cards[index], ...updates };
    handleChange('cards', cards);
  };

  const removeCard = (index) => {
    const cards = [...(flashcardConfig.cards || [])];
    cards.splice(index, 1);
    handleChange('cards', cards);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Shuffle Cards</Label>
        <Switch
          checked={flashcardConfig.shuffle !== false}
          onCheckedChange={(v) => handleChange('shuffle', v)}
        />
      </div>

      <div className="flex items-center justify-between">
        <Label>Track Mastery</Label>
        <Switch
          checked={flashcardConfig.track_mastery !== false}
          onCheckedChange={(v) => handleChange('track_mastery', v)}
        />
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <Label>Cards ({(flashcardConfig.cards || []).length})</Label>
          <Button size="sm" onClick={addCard}>
            <Plus className="h-4 w-4 mr-1" /> Add Card
          </Button>
        </div>

        <div className="space-y-3">
          {(flashcardConfig.cards || []).map((card, idx) => (
            <Card key={card.id} className="border-slate-200">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Card {idx + 1}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 hover:text-red-600"
                    onClick={() => removeCard(idx)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Front (Question/Term)</Label>
                  <Textarea
                    value={card.front_text || ''}
                    onChange={(e) => updateCard(idx, { front_text: e.target.value })}
                    placeholder="What appears on the front..."
                    rows={2}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Back (Answer/Definition)</Label>
                  <Textarea
                    value={card.back_text || ''}
                    onChange={(e) => updateCard(idx, { back_text: e.target.value })}
                    placeholder="What appears on the back..."
                    rows={2}
                  />
                </div>
                <details className="text-xs" data-testid={`flashcard-images-${idx}`}>
                  <summary className="cursor-pointer text-[#20acf6]">Images (optional)</summary>
                  <div className="space-y-3 pt-2">
                    <MediaUploadField label="Front image" value={card.front_image_url} onChange={(v) => updateCard(idx, { front_image_url: v })}
                      alt={card.front_image_alt} onAltChange={(v) => updateCard(idx, { front_image_alt: v })} testId={`flashcard-front-${idx}`} />
                    <MediaUploadField label="Back image" value={card.back_image_url} onChange={(v) => updateCard(idx, { back_image_url: v })}
                      alt={card.back_image_alt} onAltChange={(v) => updateCard(idx, { back_image_alt: v })} testId={`flashcard-back-${idx}`} />
                  </div>
                </details>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// Fill Gaps Config
const FillGapsConfig = ({ config, onChange }) => {
  const fillConfig = config?.fill_gaps || {};

  const handleChange = (key, value) => {
    onChange({
      config: {
        ...config,
        fill_gaps: { ...fillConfig, [key]: value },
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Template Text</Label>
        <Textarea
          value={fillConfig.template_text || ''}
          onChange={(e) => handleChange('template_text', e.target.value)}
          placeholder="Enter text with {gap} placeholders for missing words. E.g., 'The {gap} jumped over the {gap} dog.'"
          rows={4}
        />
        <p className="text-xs text-slate-500">
          Use {'{gap}'} to mark where words should be filled in
        </p>
      </div>

      <div className="space-y-2">
        <Label>Word Options (one per line)</Label>
        <Textarea
          value={(fillConfig.word_options || []).join('\n')}
          onChange={(e) => handleChange('word_options', e.target.value.split('\n').filter(w => w.trim()))}
          placeholder="Enter word options, including correct answers and distractors..."
          rows={4}
        />
        <p className="text-xs text-slate-500">
          Include correct answers and distractor words
        </p>
      </div>

      <div className="flex items-center justify-between">
        <Label>Show Instant Feedback</Label>
        <Switch
          checked={fillConfig.show_instant_feedback !== false}
          onCheckedChange={(v) => handleChange('show_instant_feedback', v)}
        />
      </div>
    </div>
  );
};

// Matching Config
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const MatchingModeSwitch = ({ mode, onSelect }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="matching-mode-switch">
    {[
      ['one_to_one', '1-to-1 Pairing', 'Term A → Definition A'],
      ['categorise', 'Categorisation', 'Category → many scenarios'],
      ['mixed', 'Mixed', 'Pairs and categories together'],
    ].map(([value, title, hint]) => (
      <button
        key={value}
        type="button"
        onClick={() => onSelect(value)}
        className={`p-3 rounded-lg border-2 text-left transition-colors ${mode === value ? 'border-[#20acf6] bg-[#20acf6]/5' : 'border-slate-200 hover:border-slate-300'}`}
        data-testid={`matching-mode-${value}`}
        aria-pressed={mode === value}
      >
        <p className="text-sm font-semibold text-[#2a3e5d]">{title}</p>
        <p className="text-xs text-slate-500">{hint}</p>
      </button>
    ))}
  </div>
);

const PairImage = ({ pair, onPatch, testId }) => (
  <details className="w-full text-xs" data-testid={testId}>
    <summary className="cursor-pointer text-[#20acf6]">Image (optional)</summary>
    <div className="pt-2">
      <MediaUploadField label="Item image" value={pair.left_image_url} onChange={(v) => onPatch({ left_image_url: v })}
        alt={pair.left_image_alt} onAltChange={(v) => onPatch({ left_image_alt: v })} testId={`${testId}-field`} />
    </div>
  </details>
);

const OneToOneEditor = ({ pairs, setPairs }) => (
  <div className="space-y-2">
    <p className="text-xs text-slate-500">Enter each term and its single correct match. Learners must place each term on its own match.</p>
    {pairs.map((pair, idx) => (
      <div key={pair.id} className="flex flex-wrap items-center gap-2" data-testid={`match-pair-row-${idx}`}>
        <Input value={pair.left_item || ''} onChange={(e) => setPairs(pairs.map((p, i) => i === idx ? { ...p, left_item: e.target.value } : p))} placeholder="Enter term (e.g. Jane Smith)" data-testid={`match-pair-left-${idx}`} />
        <span className="text-slate-400">→</span>
        <Input value={pair.right_item || ''} onChange={(e) => setPairs(pairs.map((p, i) => i === idx ? { ...p, right_item: e.target.value } : p))} placeholder="Enter its match (e.g. Personal Data)" data-testid={`match-pair-right-${idx}`} />
        <Button variant="ghost" size="icon" onClick={() => setPairs(pairs.filter((_, i) => i !== idx))} data-testid={`match-pair-delete-${idx}`}><Trash2 className="h-4 w-4" /></Button>
        <PairImage pair={pair} onPatch={(patch) => setPairs(pairs.map((p, i) => i === idx ? { ...p, ...patch } : p))} testId={`match-pair-img-${idx}`} />
      </div>
    ))}
    <Button size="sm" variant="outline" onClick={() => setPairs([...pairs, { id: uid(), left_item: '', right_item: '' }])} data-testid="match-add-pair-btn">
      <Plus className="h-4 w-4 mr-1" /> Add Pair
    </Button>
  </div>
);

const CategoriseEditor = ({ categories, pairs, save, mixed = false }) => {
  const word = mixed ? 'Target' : 'Category';
  const scenariosOf = (cat) => pairs.filter(p => p.category_id === cat.id);
  const renameCategory = (cat, name) => save(
    categories.map(c => c.id === cat.id ? { ...c, name } : c),
    pairs.map(p => p.category_id === cat.id ? { ...p, right_item: name } : p),
  );
  return (
    <div className="space-y-3">
      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
        <p className="text-xs text-blue-700">
          {mixed
            ? <><strong>Mixed mode:</strong> each target with one item works as a 1-to-1 pair; a target with several items works as a category. Any item listed under a target is marked correct when dropped there.</>
            : <><strong>Note:</strong> Any scenario listed under a category will be marked correct when the learner drops it into that category.</>}
        </p>
      </div>
      {categories.map((cat, ci) => (
        <Card key={cat.id} className="border-slate-200" data-testid={`match-category-${ci}`}>
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input value={cat.name} onChange={(e) => renameCategory(cat, e.target.value)} placeholder={mixed ? 'Enter target (e.g. Personal Data or Money and budgeting)' : 'Enter category (e.g. Money and budgeting)'} className="font-medium" data-testid={`match-category-name-${ci}`} />
              {mixed && (
                <Badge variant="outline" className={`shrink-0 text-[10px] ${scenariosOf(cat).length > 1 ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`} data-testid={`match-target-kind-${ci}`}>
                  {scenariosOf(cat).length > 1 ? 'Category' : '1-to-1 pair'}
                </Badge>
              )}
              <Button variant="ghost" size="icon" onClick={() => save(categories.filter(c => c.id !== cat.id), pairs.filter(p => p.category_id !== cat.id))} data-testid={`match-category-delete-${ci}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="pl-4 border-l-2 border-[#20acf6]/30 space-y-2">
              {scenariosOf(cat).map((p, si) => (
                <div key={p.id} className="flex flex-wrap items-center gap-2">
                  <Input className="flex-1 min-w-[160px]" value={p.left_item || ''} onChange={(e) => save(categories, pairs.map(x => x.id === p.id ? { ...x, left_item: e.target.value } : x))} placeholder={mixed ? 'Enter item' : 'Enter scenario'} data-testid={`match-scenario-${ci}-${si}`} />
                  <Button variant="ghost" size="icon" onClick={() => save(categories, pairs.filter(x => x.id !== p.id))} data-testid={`match-scenario-delete-${ci}-${si}`}><X className="h-4 w-4" /></Button>
                  <PairImage pair={p} onPatch={(patch) => save(categories, pairs.map(x => x.id === p.id ? { ...x, ...patch } : x))} testId={`match-scenario-img-${ci}-${si}`} />
                </div>
              ))}
              {scenariosOf(cat).length === 0 && <p className="text-xs text-amber-600">Add at least one {mixed ? 'item' : 'scenario'} to this {word.toLowerCase()}.</p>}
              <Button size="sm" variant="ghost" className="text-[#20acf6]" onClick={() => save(categories, [...pairs, { id: uid(), left_item: '', right_item: cat.name, category_id: cat.id }])} data-testid={`match-add-scenario-${ci}`}>
                <Plus className="h-4 w-4 mr-1" /> {mixed ? 'Add Item' : 'Add Scenario'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      <Button size="sm" variant="outline" onClick={() => save([...categories, { id: uid(), name: '' }], pairs)} data-testid="match-add-category-btn">
        <Plus className="h-4 w-4 mr-1" /> Add {word}
      </Button>
    </div>
  );
};

// Group existing pairs into categories (by right_item) for categorisation mode
const deriveCategories = (matchConfig) => {
  const pairs = matchConfig.pairs || [];
  if (matchConfig.categories?.length) {
    return { categories: matchConfig.categories, pairs };
  }
  const categories = [];
  const byName = {};
  const withIds = pairs.map(p => {
    const name = (p.right_item || '').trim();
    if (!byName[name]) { byName[name] = { id: uid(), name }; categories.push(byName[name]); }
    return { ...p, category_id: byName[name].id };
  });
  return { categories, pairs: withIds };
};

const MatchingConfig = ({ config, onChange }) => {
  const matchConfig = config?.matching || { pairs: [] };
  const mode = matchConfig.mode || 'one_to_one';
  const pairs = matchConfig.pairs || [];

  const update = (patch) => onChange({ config: { ...config, matching: { ...matchConfig, ...patch } } });

  const selectMode = (next) => {
    if (next === mode) return;
    if (next !== 'one_to_one') update({ mode: next, ...deriveCategories(mode === 'one_to_one' ? { ...matchConfig, categories: null } : matchConfig) });
    else update({ mode: next });
  };

  const cat = mode !== 'one_to_one' ? deriveCategories(matchConfig) : null;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Matching Mode <span className="text-red-500">*</span></Label>
        <MatchingModeSwitch mode={mode} onSelect={selectMode} />
      </div>

      <div className="space-y-2">
        <Label>Instructions</Label>
        <Textarea
          value={matchConfig.instructions || ''}
          onChange={(e) => update({ instructions: e.target.value })}
          placeholder={mode === 'categorise' ? 'Place each scenario into the category it belongs to.' : mode === 'mixed' ? 'Match each item to its correct pair or category.' : 'Match each term to its correct definition.'}
          rows={2}
        />
      </div>

      <div className="border-t pt-4">
        {mode !== 'one_to_one' ? (
          <CategoriseEditor mixed={mode === 'mixed'} categories={cat.categories} pairs={cat.pairs} save={(categories, nextPairs) => update({ categories, pairs: nextPairs })} />
        ) : (
          <OneToOneEditor pairs={pairs} setPairs={(nextPairs) => update({ pairs: nextPairs })} />
        )}
      </div>
    </div>
  );
};

// Case Study Config
const CaseStudyConfig = ({ config, onChange }) => {
  const caseConfig = config?.case_study || {};

  const handleChange = (key, value) => {
    onChange({
      config: {
        ...config,
        case_study: { ...caseConfig, [key]: value },
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Title</Label>
        <Input
          value={caseConfig.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="Case Study Title"
        />
      </div>

      <div className="space-y-2">
        <Label>Scenario Description</Label>
        <Textarea
          value={caseConfig.scenario_text || ''}
          onChange={(e) => handleChange('scenario_text', e.target.value)}
          placeholder="Describe the scenario in detail..."
          rows={6}
        />
      </div>

      <MediaUploadField label="Scenario Image (optional)" value={caseConfig.scenario_image_url} onChange={(v) => handleChange('scenario_image_url', v)}
        alt={caseConfig.scenario_image_alt} onAltChange={(v) => handleChange('scenario_image_alt', v)} testId="case-image" />

      <div className="space-y-2">
        <Label>Reflective Questions (one per line)</Label>
        <Textarea
          value={(caseConfig.reflective_questions || []).join('\n')}
          onChange={(e) => handleChange('reflective_questions', e.target.value.split('\n').filter(q => q.trim()))}
          placeholder="What would you do in this situation?&#10;What factors did you consider?"
          rows={4}
        />
      </div>

      <div className="flex items-center justify-between">
        <Label>Include Mini-Quiz</Label>
        <Switch
          checked={caseConfig.has_quiz || false}
          onCheckedChange={(v) => handleChange('has_quiz', v)}
        />
      </div>
    </div>
  );
};

// Text Content Config

const TextContentConfig = ({ config, onChange }) => {
  const textConfig = config?.text || {};

  const handleContentChange = (html) => {
    onChange({
      config: {
        ...config,
        text: { ...textConfig, content: html },
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Content</Label>
        <RichTextEditor
          content={textConfig.content || ''}
          onChange={handleContentChange}
          placeholder="Enter your text content here..."
        />
        <p className="text-xs text-slate-500">
          Use the toolbar to format text with headings, bold, italic, lists, and links.
        </p>
      </div>
    </div>
  );
};

// Reflection Journal Config
const ReflectionJournalConfig = ({ config, onChange }) => {
  const journalConfig = config?.reflection_journal || {};

  const handleChange = (key, value) => {
    onChange({
      config: {
        ...config,
        reflection_journal: { ...journalConfig, [key]: value },
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Reflection Prompt</Label>
        <Textarea
          value={journalConfig.prompt || ''}
          onChange={(e) => handleChange('prompt', e.target.value)}
          placeholder="What prompt should guide the learner's reflection?"
          rows={4}
        />
      </div>

      <div className="space-y-2">
        <Label>Minimum Words (optional)</Label>
        <Input
          type="number"
          value={journalConfig.min_words || ''}
          onChange={(e) => handleChange('min_words', parseInt(e.target.value) || null)}
          placeholder="No minimum"
        />
      </div>

      <div className="space-y-2">
        <Label>Placeholder Text</Label>
        <Input
          value={journalConfig.placeholder_text || ''}
          onChange={(e) => handleChange('placeholder_text', e.target.value)}
          placeholder="Write your reflection here..."
        />
      </div>
    </div>
  );
};

// Audio Config
const AudioConfig = ({ config, onChange }) => {
  const audioConfig = config?.audio || {};

  const handleChange = (key, value) => {
    onChange({
      config: {
        ...config,
        audio: { ...audioConfig, [key]: value },
      },
    });
  };

  return (
    <div className="space-y-4">
      <MediaUploadField label="Audio File" kind="audio" value={audioConfig.file_url} onChange={(v) => handleChange('file_url', v)} testId="audio-file" required />

      <div className="space-y-2">
        <Label>Transcript <span className="text-slate-400 font-normal text-xs">(recommended for accessibility)</span></Label>
        <div data-testid="audio-transcript">
          <RichTextEditor content={audioConfig.transcript || ''} onChange={(html) => handleChange('transcript', html)} placeholder="Audio transcript for accessibility..." />
        </div>
      </div>
    </div>
  );
};

// Downloadable Config
const DownloadableConfig = ({ config, onChange }) => {
  const downloadConfig = config?.downloadable || {};

  const handleChange = (key, value) => {
    onChange({
      config: {
        ...config,
        downloadable: { ...downloadConfig, [key]: value },
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>File URL</Label>
        <Input
          value={downloadConfig.file_url || ''}
          onChange={(e) => handleChange('file_url', e.target.value)}
          placeholder="https://..."
        />
      </div>

      <div className="space-y-2">
        <Label>File Name</Label>
        <Input
          value={downloadConfig.file_name || ''}
          onChange={(e) => handleChange('file_name', e.target.value)}
          placeholder="document.pdf"
        />
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={downloadConfig.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Describe what this resource contains..."
          rows={3}
        />
      </div>

      <div className="flex items-center justify-between">
        <Label>Track Downloads</Label>
        <Switch
          checked={downloadConfig.track_downloads !== false}
          onCheckedChange={(v) => handleChange('track_downloads', v)}
        />
      </div>
    </div>
  );
};

// Drag Drop Task Config
const DragDropTaskConfig = ({ config, onChange }) => {
  const taskConfig = config?.drag_drop_task || { drop_zones: [], draggable_items: [] };

  const handleChange = (key, value) => {
    onChange({
      config: {
        ...config,
        drag_drop_task: { ...taskConfig, [key]: value },
      },
    });
  };

  const addZone = () => {
    handleChange('drop_zones', [
      ...(taskConfig.drop_zones || []),
      { id: Date.now().toString(), label: '', correct_items: [] }
    ]);
  };

  const addItem = () => {
    handleChange('draggable_items', [
      ...(taskConfig.draggable_items || []),
      { id: Date.now().toString(), text: '' }
    ]);
  };

  const zones = taskConfig.drop_zones || [];
  const items = taskConfig.draggable_items || [];
  const zoneOf = (itemId) => (zones.find(z => (z.correct_items || []).includes(itemId)) || {}).id || '';
  const assignItem = (itemId, zoneId) => handleChange('drop_zones', zones.map(z => {
    const rest = (z.correct_items || []).filter(id => id !== itemId);
    return { ...z, correct_items: z.id === zoneId ? [...rest, itemId] : rest };
  }));
  const removeItem = (itemId) => onChange({
    config: {
      ...config,
      drag_drop_task: {
        ...taskConfig,
        draggable_items: items.filter(it => it.id !== itemId),
        drop_zones: zones.map(z => ({ ...z, correct_items: (z.correct_items || []).filter(id => id !== itemId) })),
      },
    },
  });

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Task Description</Label>
        <Textarea
          value={taskConfig.task_description || ''}
          onChange={(e) => handleChange('task_description', e.target.value)}
          placeholder="Describe the task..."
          rows={3}
        />
      </div>

      <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
        <p className="text-xs text-blue-700">
          <strong>How it works:</strong> Create categories, then add items and choose which category each belongs to. Note: any item dropped into its category will be marked correct — order does not matter.
        </p>
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <Label>Categories (Drop Zones) <span className="text-red-500">*</span></Label>
          <Button size="sm" onClick={addZone} data-testid="dd-add-zone-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Category
          </Button>
        </div>
        {zones.length === 0 && <p className="text-xs text-slate-500">Add at least one category, e.g. "Good Practice".</p>}
        {zones.map((zone, idx) => (
          <div key={zone.id} className="flex items-center gap-2 mb-2">
            <Input
              value={zone.label}
              onChange={(e) => handleChange('drop_zones', zones.map((z, i) => i === idx ? { ...z, label: e.target.value } : z))}
              placeholder="Enter category name"
              data-testid={`dd-zone-label-${idx}`}
            />
            <span className="text-xs text-slate-500 whitespace-nowrap">{(zone.correct_items || []).length} {(zone.correct_items || []).length === 1 ? 'item' : 'items'}</span>
            <Button variant="ghost" size="icon" onClick={() => handleChange('drop_zones', zones.filter((_, i) => i !== idx))} data-testid={`dd-zone-delete-${idx}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <Label>Items to Sort <span className="text-red-500">*</span></Label>
          <Button size="sm" onClick={addItem} data-testid="dd-add-item-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Item
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-2">Enter the item text, then select the category it belongs to. Any item dropped into its category is marked correct.</p>
        {items.map((item, idx) => {
          const zoneId = zoneOf(item.id);
          return (
            <div key={item.id} className={`flex flex-wrap items-center gap-2 mb-2 p-2 rounded-lg border ${zoneId ? 'border-slate-200' : 'border-amber-300 bg-amber-50'}`}>
              <Input
                value={item.text}
                onChange={(e) => handleChange('draggable_items', items.map((it, i) => i === idx ? { ...it, text: e.target.value } : it))}
                placeholder="Enter item / scenario"
                className="w-full"
                data-testid={`dd-item-text-${idx}`}
              />
              <select
                value={zoneId}
                onChange={(e) => assignItem(item.id, e.target.value)}
                className="h-9 rounded-md border border-slate-200 px-2 text-sm flex-1 min-w-[140px]"
                data-testid={`dd-item-zone-${idx}`}
              >
                <option value="">Select category...</option>
                {zones.map(z => <option key={z.id} value={z.id}>{z.label || 'Untitled category'}</option>)}
              </select>
              <Button variant="ghost" size="icon" onClick={() => removeItem(item.id)} data-testid={`dd-item-delete-${idx}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
              <details className="w-full text-xs" data-testid={`dd-item-image-${idx}`}>
                <summary className="cursor-pointer text-[#20acf6]">Image (optional)</summary>
                <div className="pt-2">
                  <MediaUploadField label="Item image" value={item.image_url}
                    onChange={(v) => handleChange('draggable_items', items.map((it, i) => i === idx ? { ...it, image_url: v } : it))}
                    alt={item.image_alt}
                    onAltChange={(v) => handleChange('draggable_items', items.map((it, i) => i === idx ? { ...it, image_alt: v } : it))}
                    testId={`dd-item-img-${idx}`} />
                </div>
              </details>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Image Hotspot Config — click-to-place visual editor
const ImageHotspotConfig = ({ config, onChange }) => {
  const hotspotConfig = config?.image_hotspot || { hotspots: [] };
  const imageRef = React.useRef(null);
  const [draggingIdx, setDraggingIdx] = React.useState(null);
  const [editIdx, setEditIdx] = React.useState(null);

  const handleChange = (key, value) => {
    onChange({ config: { ...config, image_hotspot: { ...hotspotConfig, [key]: value } } });
  };

  const updateHotspot = (idx, updates) => {
    const spots = [...(hotspotConfig.hotspots || [])];
    spots[idx] = { ...spots[idx], ...updates };
    handleChange('hotspots', spots);
  };

  const handleImageClick = (e) => {
    if (draggingIdx !== null) return;
    const rect = imageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    const newHotspot = { id: Date.now().toString(), x, y, title: '', content: '' };
    const spots = [...(hotspotConfig.hotspots || []), newHotspot];
    handleChange('hotspots', spots);
    setEditIdx(spots.length - 1);
  };

  const handleDrag = (e, idx) => {
    e.preventDefault();
    const rect = imageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(100, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
    const y = Math.max(0, Math.min(100, Math.round(((e.clientY - rect.top) / rect.height) * 100)));
    updateHotspot(idx, { x, y });
  };

  const removeHotspot = (idx) => {
    const spots = (hotspotConfig.hotspots || []).filter((_, i) => i !== idx);
    handleChange('hotspots', spots);
    if (editIdx === idx) setEditIdx(null);
    else if (editIdx > idx) setEditIdx(editIdx - 1);
  };

  return (
    <div className="space-y-4">
      <MediaUploadField label="Image" value={hotspotConfig.image_url} onChange={(v) => handleChange('image_url', v)}
        alt={hotspotConfig.image_alt} onAltChange={(v) => handleChange('image_alt', v)} testId="hotspot-image" required />

      <div className="space-y-2">
        <Label>Instructions</Label>
        <Textarea
          value={hotspotConfig.instructions || ''}
          onChange={(e) => handleChange('instructions', e.target.value)}
          placeholder="Click on the highlighted areas to learn more."
          rows={2}
        />
      </div>

      {/* Visual hotspot editor */}
      {hotspotConfig.image_url && (
        <div className="space-y-2">
          <Label>Click on the image to add hotspots. Drag to reposition.</Label>
          <div
            className="relative border-2 border-dashed border-slate-300 rounded-xl overflow-hidden cursor-crosshair bg-slate-50"
            onClick={handleImageClick}
            data-testid="hotspot-image-editor"
          >
            <img
              ref={imageRef}
              src={mediaUrl(hotspotConfig.image_url)}
              alt={hotspotConfig.image_alt || 'Hotspot editor'}
              className="w-full h-auto block select-none pointer-events-none"
              draggable={false}
            />
            {(hotspotConfig.hotspots || []).map((hs, idx) => (
              <div
                key={hs.id}
                className={`absolute flex items-center justify-center rounded-full border-2 cursor-grab active:cursor-grabbing transition-shadow select-none ${
                  editIdx === idx
                    ? 'border-[#20acf6] bg-[#20acf6]/40 ring-2 ring-[#20acf6] shadow-lg z-20'
                    : 'border-[#02d1d4] bg-[#02d1d4]/30 hover:shadow-md z-10'
                }`}
                style={{
                  left: `${hs.x}%`, top: `${hs.y}%`,
                  width: '28px', height: '28px',
                  transform: 'translate(-50%, -50%)',
                }}
                onClick={(e) => { e.stopPropagation(); setEditIdx(editIdx === idx ? null : idx); }}
                draggable
                onDragStart={(e) => { setDraggingIdx(idx); e.dataTransfer.setDragImage(new Image(), 0, 0); }}
                onDrag={(e) => { if (e.clientX > 0 && e.clientY > 0) handleDrag(e, idx); }}
                onDragEnd={() => setDraggingIdx(null)}
                data-testid={`hotspot-pin-${idx}`}
              >
                <span className="text-[10px] font-bold text-white drop-shadow">{idx + 1}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">{(hotspotConfig.hotspots || []).length} hotspot{(hotspotConfig.hotspots || []).length !== 1 ? 's' : ''} placed</p>
        </div>
      )}

      {/* Hotspot detail editor */}
      {editIdx !== null && (hotspotConfig.hotspots || [])[editIdx] && (() => {
        const hs = hotspotConfig.hotspots[editIdx];
        return (
          <Card className="border-[#20acf6]/30 bg-[#20acf6]/5">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[#2a3e5d]">Hotspot {editIdx + 1}</span>
                <div className="flex gap-1">
                  <span className="text-[10px] text-slate-400">({hs.x}%, {hs.y}%)</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeHotspot(editIdx)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Title</Label>
                <Input value={hs.title} onChange={(e) => updateHotspot(editIdx, { title: e.target.value })} placeholder="Hotspot title" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <Textarea value={hs.content} onChange={(e) => updateHotspot(editIdx, { content: e.target.value })} placeholder="Content shown when clicked..." rows={2} />
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* List all hotspots */}
      {(hotspotConfig.hotspots || []).length > 0 && editIdx === null && (
        <div className="space-y-1">
          {(hotspotConfig.hotspots || []).map((hs, idx) => (
            <div
              key={hs.id}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 text-sm cursor-pointer hover:bg-slate-100"
              onClick={() => setEditIdx(idx)}
            >
              <div className="w-5 h-5 rounded-full bg-[#02d1d4]/30 border border-[#02d1d4] flex items-center justify-center">
                <span className="text-[9px] font-bold text-[#2a3e5d]">{idx + 1}</span>
              </div>
              <span className="flex-1 truncate text-slate-700">{hs.title || `Hotspot ${idx + 1}`}</span>
              <span className="text-[10px] text-slate-400">({hs.x}%, {hs.y}%)</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); removeHotspot(idx); }}>
                <Trash2 className="h-3 w-3 text-slate-400" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// Trainer Tip Config
const TIP_TYPES = [
  { value: 'welcome', label: 'Welcome', default_text: "Hi there! Let\u2019s get started with this module. I\u2019ll be here to help you along the way." },
  { value: 'feedback_pass', label: 'Quiz Feedback (Pass)', default_text: "Great work! You really know your stuff. Keep it up!" },
  { value: 'feedback_fail', label: 'Quiz Feedback (Fail)', default_text: "Don\u2019t worry \u2014 let\u2019s review the material together and try again. You\u2019ve got this!" },
  { value: 'summary', label: 'Section Summary', default_text: "That was a key point. Remember, this is important for your practice in care settings." },
  { value: 'encouragement', label: 'Encouragement', default_text: "You\u2019re making great progress. Keep going \u2014 you\u2019re almost there!" },
  { value: 'custom', label: 'Custom', default_text: '' },
];

const TrainerTipConfig = ({ config, onChange }) => {
  // Read from nested or flat
  const tipConfig = config?.trainer_tip || {};
  const tip_type = config?.tip_type || tipConfig.tip_type || 'welcome';
  const text = config?.text || tipConfig.text || '';
  const ace_image_url = config?.ace_image_url || tipConfig.ace_image_url || '';

  const handleChange = (key, value) => {
    // Save both flat AND nested for compatibility
    onChange({
      config: {
        ...config,
        [key]: value,
        trainer_tip: { ...tipConfig, [key]: value },
      },
    });
  };

  const handleTipTypeChange = (newTipType) => {
    const preset = TIP_TYPES.find(t => t.value === newTipType);
    const newText = (preset && newTipType !== 'custom' && !text) ? preset.default_text : text;
    // Single combined update to avoid tip_type being lost
    onChange({
      config: {
        ...config,
        tip_type: newTipType,
        text: newText,
        trainer_tip: { ...tipConfig, tip_type: newTipType, text: newText },
      },
    });
  };

  const applyDefaultText = () => {
    const preset = TIP_TYPES.find(t => t.value === tip_type);
    if (preset) handleChange('text', preset.default_text);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Tip Type</Label>
        <Select
          value={tip_type}
          onValueChange={handleTipTypeChange}
        >
          <SelectTrigger data-testid="trainer-tip-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIP_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Tip Text</Label>
          {tip_type && tip_type !== 'custom' && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs text-[#20acf6] h-6"
              onClick={applyDefaultText}
            >
              Reset to default
            </Button>
          )}
        </div>
        <RichTextEditor
          key={`tip-editor-${tip_type}-${text?.length || 0}`}
          content={text}
          onChange={(html) => handleChange('text', html)}
          placeholder="Enter the tip text Ace will say..."
        />
        <p className="text-xs text-slate-500">
          Supports bold, italic, and bullet points
        </p>
      </div>

      <div className="space-y-2" data-testid="trainer-tip-ace-image">
        <MediaUploadField label="Ace Image (optional)" value={ace_image_url} onChange={(v) => handleChange('ace_image_url', v)}
          alt={config?.ace_image_alt || tipConfig.ace_image_alt} onAltChange={(v) => handleChange('ace_image_alt', v)}
          placeholder="Leave blank to use default Ace illustration" testId="tip-image" />
        <p className="text-xs text-slate-500">
          Override the default Ace character image. Can be updated later via media library.
        </p>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// Timeline Config
// ═══════════════════════════════════════════
const TimelineConfig = ({ config, onChange }) => {
  const tc = config?.timeline || {};
  const events = tc.events || [{ id: '1', label: '', correct_position: 1 }];

  const handleChange = (key, value) => {
    onChange({ config: { ...config, timeline: { ...tc, [key]: value } } });
  };

  const updateEvent = (idx, key, val) => {
    const updated = [...events];
    updated[idx] = { ...updated[idx], [key]: val };
    handleChange('events', updated);
  };

  const addEvent = () => {
    handleChange('events', [...events, { id: String(events.length + 1), label: '', correct_position: events.length + 1 }]);
  };

  const removeEvent = (idx) => {
    handleChange('events', events.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Instructions</Label>
        <Input value={tc.instructions || ''} onChange={(e) => handleChange('instructions', e.target.value)} placeholder="e.g. Put the safeguarding steps in order" />
      </div>
      <div className="space-y-2">
        <Label>Events (in correct order)</Label>
        <p className="text-xs text-slate-500">Add events in the correct chronological order. They will be shuffled for the learner.</p>
        {events.map((ev, idx) => (
          <div key={ev.id || idx} className="flex items-center gap-2">
            <span className="text-xs text-slate-400 w-6">{idx + 1}.</span>
            <Input value={ev.label} onChange={(e) => updateEvent(idx, 'label', e.target.value)} placeholder={`Event ${idx + 1}`} className="flex-1" />
            <Button type="button" variant="ghost" size="sm" onClick={() => removeEvent(idx)}><span className="text-red-400 text-xs">Remove</span></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addEvent}>+ Add Event</Button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// Label Diagram Config
// ═══════════════════════════════════════════
const LabelDiagramConfig = ({ config, onChange }) => {
  const lc = config?.label_diagram || {};
  const labels = lc.labels || [];

  const handleChange = (key, value) => {
    onChange({ config: { ...config, label_diagram: { ...lc, [key]: value } } });
  };

  const updateLabel = (idx, key, val) => {
    const updated = [...labels];
    updated[idx] = { ...updated[idx], [key]: val };
    handleChange('labels', updated);
  };

  const addLabel = () => {
    handleChange('labels', [...labels, { id: String(labels.length + 1), text: '', x: 50, y: 50 }]);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <MediaUploadField label="Diagram Image" value={lc.image_url} onChange={(v) => handleChange('image_url', v)}
          alt={lc.image_alt} onAltChange={(v) => handleChange('image_alt', v)} testId="diagram-image" required />
      </div>
      <div className="space-y-2">
        <Label>Instructions</Label>
        <Input value={lc.instructions || ''} onChange={(e) => handleChange('instructions', e.target.value)} placeholder="e.g. Label the parts of a care plan" />
      </div>
      <div className="space-y-2">
        <Label>Labels</Label>
        <p className="text-xs text-slate-500">Define labels and their correct positions (x, y as % of image dimensions). Learners will drag labels to the correct spots.</p>
        {labels.map((label, idx) => (
          <div key={label.id || idx} className="grid grid-cols-4 gap-2 items-center">
            <Input value={label.text} onChange={(e) => updateLabel(idx, 'text', e.target.value)} placeholder="Label text" className="col-span-2" />
            <Input type="number" value={label.x} onChange={(e) => updateLabel(idx, 'x', Number(e.target.value))} placeholder="X %" />
            <Input type="number" value={label.y} onChange={(e) => updateLabel(idx, 'y', Number(e.target.value))} placeholder="Y %" />
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addLabel}>+ Add Label</Button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// Sort Order Config
// ═══════════════════════════════════════════
const SortOrderConfig = ({ config, onChange }) => {
  const sc = config?.sort_order || {};
  const items = sc.items || [''];

  const handleChange = (key, value) => {
    onChange({ config: { ...config, sort_order: { ...sc, [key]: value } } });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Instructions</Label>
        <Input value={sc.instructions || ''} onChange={(e) => handleChange('instructions', e.target.value)} placeholder="e.g. Put the safeguarding steps in order" />
      </div>
      <div className="space-y-2">
        <Label>Items (in correct order)</Label>
        <p className="text-xs text-slate-500">Enter items in the correct order. They will be shuffled for the learner.</p>
        <Textarea
          value={items.join('\n')}
          onChange={(e) => handleChange('items', e.target.value.split('\n').filter(x => x.trim()))}
          placeholder="Step 1&#10;Step 2&#10;Step 3"
          rows={6}
        />
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// Decision Tree Config
// ═══════════════════════════════════════════
const DecisionTreeConfig = ({ config, onChange }) => {
  const dt = config?.decision_tree || {};
  const nodes = dt.nodes || [{ id: 'start', text: '', choices: [{ label: '', next_node: '' }] }];

  const handleChange = (key, value) => {
    onChange({ config: { ...config, decision_tree: { ...dt, [key]: value } } });
  };

  const updateNode = (nodeIdx, key, val) => {
    const updated = [...nodes];
    updated[nodeIdx] = { ...updated[nodeIdx], [key]: val };
    handleChange('nodes', updated);
  };

  const updateChoice = (nodeIdx, choiceIdx, key, val) => {
    const updated = [...nodes];
    const choices = [...(updated[nodeIdx].choices || [])];
    // Migrate legacy keys (text/next) to label/next_node so edits always take effect
    const { text, next, ...rest } = choices[choiceIdx] || {};
    choices[choiceIdx] = { ...rest, label: rest.label ?? text ?? '', next_node: rest.next_node ?? next ?? '', [key]: val };
    updated[nodeIdx] = { ...updated[nodeIdx], choices };
    handleChange('nodes', updated);
  };

  const addNode = () => {
    const id = `node_${nodes.length}`;
    handleChange('nodes', [...nodes, { id, text: '', choices: [{ label: '', next_node: '' }] }]);
  };

  const addChoice = (nodeIdx) => {
    const updated = [...nodes];
    updated[nodeIdx] = { ...updated[nodeIdx], choices: [...(updated[nodeIdx].choices || []), { label: '', next_node: '' }] };
    handleChange('nodes', updated);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Scenario Title</Label>
        <Input value={dt.title || ''} onChange={(e) => handleChange('title', e.target.value)} placeholder="e.g. What would you do in this situation?" />
      </div>
      {nodes.map((node, ni) => (
        <div key={node.id} className="p-3 border border-slate-200 rounded-lg space-y-2">
          <Label className="text-xs text-slate-500">Node: {node.id}</Label>
          <Textarea value={node.text} onChange={(e) => updateNode(ni, 'text', e.target.value)} placeholder="Scenario text / question" rows={2} />
          {node.is_end && <p className="text-xs text-green-600 font-medium">End node (outcome)</p>}
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={node.is_end || false} onChange={(e) => updateNode(ni, 'is_end', e.target.checked)} />
            <span className="text-xs text-slate-500">This is an end/outcome node</span>
          </div>
          {!node.is_end && (
            <div className="space-y-1 pl-3 border-l-2 border-slate-200">
              <Label className="text-xs">Choices</Label>
              <p className="text-[11px] text-slate-500">Tick the correct path. Learners see each option's feedback; a wrong choice lets them try again, the correct one continues to the next step.</p>
              {(node.choices || []).map((ch, ci) => (
                <div key={ci} className={`p-2 rounded-lg border space-y-2 ${ch.is_correct ? 'border-green-300 bg-green-50/50' : 'border-slate-200'}`} data-testid={`dt-choice-${ni}-${ci}`}>
                  <div className="flex gap-2 items-center">
                    <span className="text-xs font-bold text-slate-500 w-5">{String.fromCharCode(65 + ci)}</span>
                    <Input value={ch.label ?? ch.text ?? ''} onChange={(e) => updateChoice(ni, ci, 'label', e.target.value)} placeholder="Enter option text" className="flex-1" data-testid={`dt-choice-label-${ni}-${ci}`} />
                    <select value={ch.next_node ?? ch.next ?? ''} onChange={(e) => updateChoice(ni, ci, 'next_node', e.target.value)} className="text-xs border border-slate-200 rounded px-2 h-9" data-testid={`dt-choice-next-${ni}-${ci}`}>
                      <option value="">→ End here</option>
                      {nodes.filter(n => n.id !== node.id).map(n => <option key={n.id} value={n.id}>→ {n.id}</option>)}
                    </select>
                    <button type="button" className="text-slate-400 hover:text-red-600 px-1" aria-label="Remove option"
                      onClick={() => updateNode(ni, 'choices', (node.choices || []).filter((_, i) => i !== ci))} data-testid={`dt-choice-delete-${ni}-${ci}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                    <input type="radio" name={`dt-correct-${node.id}`} checked={!!ch.is_correct} className="accent-green-600"
                      onChange={() => updateNode(ni, 'choices', (node.choices || []).map((c, i) => ({ ...c, is_correct: i === ci })))}
                      data-testid={`dt-choice-correct-${ni}-${ci}`} />
                    Correct path
                  </label>
                  <Textarea value={ch.feedback || ''} onChange={(e) => updateChoice(ni, ci, 'feedback', e.target.value)} rows={2}
                    placeholder={ch.is_correct ? 'Feedback when chosen (e.g. why this is the right approach)' : 'Feedback when chosen (e.g. why this is risky — learner will try again)'}
                    data-testid={`dt-choice-feedback-${ni}-${ci}`} />
                </div>
              ))}
              <Button type="button" variant="ghost" size="sm" onClick={() => addChoice(ni)}>+ Choice</Button>
            </div>
          )}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addNode}>+ Add Node</Button>
    </div>
  );
};

// ═══════════════════════════════════════════
// Progress Checkpoint Config
// ═══════════════════════════════════════════
const ProgressCheckpointConfig = ({ config, onChange }) => {
  const pc = config?.progress_checkpoint || {};
  const questions = pc.questions || [{ question: '', options: ['', ''], correct: 0 }];

  const handleChange = (key, value) => {
    onChange({ config: { ...config, progress_checkpoint: { ...pc, [key]: value } } });
  };

  const updateQ = (qi, key, val) => {
    const updated = [...questions];
    updated[qi] = { ...updated[qi], [key]: val };
    handleChange('questions', updated);
  };

  const addQ = () => {
    handleChange('questions', [...questions, { question: '', options: ['', ''], correct: 0 }]);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Checkpoint Message</Label>
        <Input value={pc.message || ''} onChange={(e) => handleChange('message', e.target.value)} placeholder="e.g. Before you continue, let's check your understanding" />
      </div>
      <div className="space-y-2">
        <Label>Pass Requirement</Label>
        <p className="text-xs text-slate-500">Learner must answer all questions correctly to proceed.</p>
      </div>
      <div className="space-y-3">
        <Label>Questions</Label>
        {questions.map((q, qi) => (
          <div key={qi} className="p-3 border border-slate-200 rounded-lg space-y-2">
            <Input value={q.question} onChange={(e) => updateQ(qi, 'question', e.target.value)} placeholder={`Question ${qi + 1}`} />
            <div className="space-y-1">
              {(q.options || []).map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input type="radio" name={`cp-${qi}`} checked={q.correct === oi} onChange={() => updateQ(qi, 'correct', oi)} />
                  <Input value={opt} onChange={(e) => { const opts = [...(q.options || [])]; opts[oi] = e.target.value; updateQ(qi, 'options', opts); }} placeholder={`Option ${oi + 1}`} className="flex-1" />
                </div>
              ))}
              <Button type="button" variant="ghost" size="sm" onClick={() => { const opts = [...(q.options || []), '']; updateQ(qi, 'options', opts); }}>+ Option</Button>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addQ}>+ Add Question</Button>
      </div>
    </div>
  );
};

export {
  VideoConfig, QuizConfig, FlashcardConfig, FillGapsConfig,
  MatchingConfig, CaseStudyConfig, TextContentConfig,
  ReflectionJournalConfig, AudioConfig, DownloadableConfig,
  DragDropTaskConfig, ImageHotspotConfig, TrainerTipConfig,
  TimelineConfig, LabelDiagramConfig, SortOrderConfig,
  DecisionTreeConfig, ProgressCheckpointConfig,
};
